<template>
  <div class="search-bar">
    <div class="search-bar-row">
      <span class="search-icon">
        <i class="fas fa-search"></i>
      </span>
      <input
        v-model="query"
        @input="onInput"
        @keydown.enter="search"
        type="text"
        placeholder="Search posts and announcements..."
        class="search-input"
      />
    </div>
    <div v-if="error" class="search-error">{{ error }}</div>
    <div
      v-if="results.length"
      class="search-results search-results-float"
    >
      <div v-for="item in results" :key="item.id" class="search-result">
        <router-link
          :to="`/${item.type.toLowerCase()}/${item.slug}`"
          class="search-result-link"
        >
          <span v-html="item.title.rendered"></span>
          <span class="search-type">{{ item.type }}</span>
        </router-link>
        <div class="search-excerpt" v-html="item.excerpt?.rendered || item.content?.rendered"></div>
      </div>
    </div>
    <div v-else-if="searched && !loading" class="search-no-results search-results-float">
      No results found.
    </div>
  </div>
</template>

<script>
import {
  fetchCurrentUser,
  fetchCategoriesBySlugs,
  fetchPostsByCategoryIds,
  fetchAnnouncements,
  category_from_role
} from '../utils/fetch.js';

export default {
  name: "SearchBar",
  data() {
    return {
      query: "",
      loading: false,
      error: null,
      results: [],
      searched: false,
      departmentCatIds: [],
      initialized: false,
    };
  },
  methods: {
    async initCategories() {
      if (this.initialized) return;
      try {
        const user = await fetchCurrentUser();
        const roles = user.my_roles || [];
        const slugs = roles.map(role => category_from_role[role]).filter(Boolean);
        if (!slugs.length) throw new Error("No department roles found.");
        const cats = await fetchCategoriesBySlugs(slugs);
        this.departmentCatIds = cats.map(cat => cat.id);
        this.initialized = true;
      } catch (err) {
        this.error = "Failed to load department categories: " + err.message;
      }
    },
    async search() {
      this.loading = true;
      this.error = null;
      this.results = [];
      this.searched = false;
      await this.initCategories();
      if (!this.departmentCatIds.length) {
        this.loading = false;
        this.error = "No department categories found.";
        return;
      }
      try {
        // Search posts
        const postExtra = `&search=${encodeURIComponent(this.query)}&per_page=5&_embed`;
        const posts = await fetchPostsByCategoryIds(this.departmentCatIds, postExtra);
        posts.forEach(p => (p.type = "Post"));

        // Search announcements
        const annExtra = `&search=${encodeURIComponent(this.query)}&per_page=5&_embed&categories=${this.departmentCatIds.join(',')}`;
        const announcements = await fetchAnnouncements(annExtra);
        announcements.forEach(a => (a.type = "Announcement"));

        this.results = [...posts, ...announcements];
        this.searched = true;
      } catch (err) {
        this.error = "Search failed: " + err.message;
      } finally {
        this.loading = false;
      }
    },
    onInput() {
      if (!this.query) {
        this.results = [];
        this.searched = false;
      }
    }
  }
};
</script>

<style scoped>
.search-bar {
  display: flex;
  flex-direction: column;
  gap: 0.5em;
  width: 100%;
  margin: 0;
  position: relative;
}
.search-bar-row {
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  background: #F9F8F6;
  border-radius: 32px;
  padding: 0.25em 0.5em 0.25em 0.75em;
  box-sizing: border-box;
  position: relative;
}
.search-icon {
  color: #bbb;
  font-size: 1.2em;
  margin-right: 0.5em;
  display: flex;
  align-items: center;
}
.search-input {
  padding: 0.5em 1em;
  border-radius: 32px;
  border: none;
  background: transparent;
  font-size: 1em;
  width: 100%;
  outline: none;
}
.search-btn {
  padding: 0.5em 1.2em;
  border-radius: 32px;
  border: none;
  background: var(--primary-color);
  color: #fff;
  font-size: 1em;
  cursor: pointer;
  align-self: center;
  height: 100%;
  margin-left: 0.5em;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
}
.search-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
}
.search-loading,
.search-error,
.search-no-results {
  color: #888;
  font-size: 0.95em;
  margin-top: 0.5em;
}
.search-error,
.search-no-results {
  color: #c00;
  font-size: 0.95em;
  position: absolute;
  left: 0;
  right: 0;
  top: 110%;
  background: #fff;
  border-radius: 8px;
  z-index: 1001;
  padding: 0.75em 1em;
  margin: 0;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  min-width: 250px;
  max-width: 100%;
  white-space: pre-line;
}
.search-no-results {
  color: #888;
}
.search-results,
.search-no-results {
  margin-top: 0.5em;
}
.search-results-float {
  position: absolute;
  left: 0;
  right: 0;
  top: 110%;
  background: #fff;
  border-radius: 8px;
  z-index: 1000;
  padding: 1em;
  min-width: 250px;
  max-width: 100%;
}
.search-results {
  display: flex;
  flex-direction: column;
  gap: 1em;
}
.search-result {
  background: #fafbfc;
  border-radius: 6px;
  padding: 0.75em 1em;
}
.search-result a {
  font-weight: 500;
  color: #0974b2;
  text-decoration: none;
}
.search-result a:hover {
  text-decoration: underline;
}
.search-type {
  font-size: 0.85em;
  color: #888;
  margin-left: 0.5em;
}
.search-excerpt {
  font-size: 0.95em;
  color: #555;
  margin-top: 0.3em;
}
</style>